import React from 'react';
import styled from 'styled-components';
import { FaHandshake, FaBuilding, FaUniversity, FaUsers } from 'react-icons/fa';
import { theme } from '../styles/theme';

const PartnersContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
`;

const PartnersWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const PartnersHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const PartnersText = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 48px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// Why Partner Section
const BenefitsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  grid-gap: 30px;
  margin-top: 48px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: ${theme.colors.primary};
  padding: 30px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid ${theme.colors.tertiary};
  }
`;

const BenefitIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.secondary};
  margin-bottom: 16px;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: ${theme.colors.secondary};
`;

const BenefitText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
`;

// Current Partners Section
const CurrentPartnersSection = styled.div`
  margin-top: 80px;
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 40px;
  margin-top: 48px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerCard = styled.div`
  background: ${theme.colors.primary};
  padding: 30px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid ${theme.colors.tertiary};
  }
`;

const FeaturedPartnerCard = styled(PartnerCard)`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(184, 115, 51, 0.1));
  border: 2px solid ${theme.colors.secondary};
  box-shadow: 0 5px 20px rgba(212, 175, 55, 0.3);
  grid-column: span 1;
  
  @media screen and (min-width: 769px) {
    grid-column: span 2;
  }
  
  &:hover {
    border: 2px solid ${theme.colors.tertiary};
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
  }
`;

const FeaturedBadge = styled.div`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 6px 16px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 0.85rem;
  font-weight: ${theme.typography.fontWeight.semiBold};
  margin-bottom: 16px;
`;

const PartnerLogo = styled.div`
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PartnerName = styled.h3`
  font-size: 1.2rem;
  color: ${theme.colors.secondary};
  margin-bottom: 10px;
`;

const PartnerDescription = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
  line-height: 1.5;
`;

// Join Us Section
const JoinUsSection = styled.div`
  margin-top: 80px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('/images/join-bg.jpg') no-repeat center center;
  background-size: cover;
  padding: 80px 0;
  border-radius: 10px;
`;

const JoinUsContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  padding: 0 24px;
`;

const JoinUsHeading = styled.h3`
  color: #fff;
  font-size: 2rem;
  margin-bottom: 24px;
`;

const JoinUsText = styled.p`
  color: #fff;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const JoinUsButton = styled.a`
  display: inline-block;
  background: #01bf71;
  color: #010606;
  padding: 14px 32px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: #fff;
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const Partners = () => {
  return (
    <PartnersContainer id="partners">
      <PartnersWrapper>
        <PartnersHeading>Working Together to Make Tech Matter</PartnersHeading>
        <PartnersText>
          We work with governments, local businesses, non-profits, and investors to roll out sustainable tech solutions that address real challenges and create lasting impact.
        </PartnersText>
        
        <BenefitsContainer>
          <BenefitCard>
            <BenefitIcon>
              <FaHandshake />
            </BenefitIcon>
            <BenefitTitle>Strategic Collaboration</BenefitTitle>
            <BenefitText>
              Partner with us to combine resources, expertise, and networks to achieve shared goals and maximize impact.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <BenefitIcon>
              <FaBuilding />
            </BenefitIcon>
            <BenefitTitle>Market Expansion</BenefitTitle>
            <BenefitText>
              Access new markets and customer segments through our technology platforms and community connections.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <BenefitIcon>
              <FaUniversity />
            </BenefitIcon>
            <BenefitTitle>Innovation & Research</BenefitTitle>
            <BenefitText>
              Collaborate on research and development initiatives to create cutting-edge solutions for African challenges.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <BenefitIcon>
              <FaUsers />
            </BenefitIcon>
            <BenefitTitle>Community Impact</BenefitTitle>
            <BenefitText>
              Join us in creating meaningful social impact through technology that empowers communities and improves lives.
            </BenefitText>
          </BenefitCard>
        </BenefitsContainer>
        
        <CurrentPartnersSection>
          <PartnersHeading>Current Stakeholders</PartnersHeading>
          <PartnersText>
            We're proud to collaborate with forward-thinking organizations that share our vision for technology-driven transformation in Africa.
          </PartnersText>
          
          <PartnersGrid>
            <FeaturedPartnerCard>
              <FeaturedBadge>Featured Partner</FeaturedBadge>
              <PartnerLogo>
                <img src="/images/safirst-logo.png" alt="SA1stWorkers" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </PartnerLogo>
              <PartnerName>SA1stWorkers</PartnerName>
              <PartnerDescription>
                Connecting South Africans with trusted local professionals. Our flagship platform for skilled tradespeople and service providers.
              </PartnerDescription>
            </FeaturedPartnerCard>
            
            <PartnerCard>
              <PartnerLogo>
                <img src="/images/nef-logo.png" alt="NEF" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </PartnerLogo>
              <PartnerName>National Empowerment Fund</PartnerName>
              <PartnerDescription>
                Supporting entrepreneurship and economic empowerment initiatives.
              </PartnerDescription>
            </PartnerCard>
            
            <PartnerCard>
              <PartnerLogo>
                <img src="/images/nyda-logo.png" alt="NYDA" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </PartnerLogo>
              <PartnerName>National Youth Development Agency</PartnerName>
              <PartnerDescription>
                Advancing youth development through technology and innovation.
              </PartnerDescription>
            </PartnerCard>
            
            <PartnerCard>
              <PartnerLogo>
                <img src="/images/partner-placeholder.png" alt="Partner" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </PartnerLogo>
              <PartnerName>Technology Innovation Agency</PartnerName>
              <PartnerDescription>
                Enabling technological innovation across sectors.
              </PartnerDescription>
            </PartnerCard>
            
            <PartnerCard>
              <PartnerLogo>
                <img src="/images/partner-placeholder.png" alt="Partner" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </PartnerLogo>
              <PartnerName>Local Municipality</PartnerName>
              <PartnerDescription>
                Implementing technology solutions for community development.
              </PartnerDescription>
            </PartnerCard>
          </PartnersGrid>
        </CurrentPartnersSection>
        
        <JoinUsSection>
          <JoinUsContent>
            <JoinUsHeading>Become a Partner</JoinUsHeading>
            <JoinUsText>
              Join us in our mission to build lasting impact through innovative technology solutions tailored for Africa. We're always looking for new partners who share our vision.
            </JoinUsText>
            <JoinUsButton href="/contact#contact-form">Contact Us Today</JoinUsButton>
          </JoinUsContent>
        </JoinUsSection>
      </PartnersWrapper>
    </PartnersContainer>
  );
};

export default Partners;
